import React, { useEffect, useState } from 'react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import FormHelperText from '@material-ui/core/FormHelperText';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useForm, Controller } from 'react-hook-form';
import TextField from '../../../styles/templates/DSTextField';

import { DSButton } from '../../Button/Button';
import PlatformButton from '../../Button/PlatformButton';
import { getErrorMessage, formValidators } from '../../../helpers/formErrorMessages';
import { countries } from '../../../constants/countries';
import { quickStartApp } from '../../../services/DevPortalService';
import { CreateAppError, QuickAppType } from '../../../interfaces/quickApp';
import TooltipInput from '../../Input/TooltipInput';
import { TOOLTIPS } from '../../../constants/tooltips';

import AndroidImage from '../../../assets/Android';
import AppleImage from '../../../assets/Apple';
import WebImage from '../../../assets/Web';
import useCreateAppModalStyles from './styles';

export interface CreateAppModalProps {
  onClose: () => void;
  onCreate: (app: QuickAppType) => void;
  userDetails?: { company: string; country: string };
}

const CreateAppModal: React.FC<CreateAppModalProps> = ({ onClose, userDetails, onCreate }) => {
  const {
    register,
    unregister,
    handleSubmit,
    formState: { errors },
    control,
    clearErrors,
    setError,
    setValue,
  } = useForm<QuickAppType>({
    defaultValues: { companyName: '', country: 'United Kingdom' },
  });
  const classes = useCreateAppModalStyles();
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [generalError, setGeneralError] = useState('');

  useEffect(() => {
    register('companyName', { required: true });
    register('country', { required: true });
    register('platforms');

    if (userDetails) {
      setValue('country', userDetails.country);
      setValue('companyName', userDetails.company);
    }

    return () => {
      unregister('companyName');
      unregister('country');
      unregister('platforms');
    };
  }, [register, unregister, userDetails, setValue]);

  const onModalClose = () => {
    setPlatforms([]);
    onClose();
  };

  const onPlatformClick = (platform: string) => {
    clearErrors('platforms');
    if (platforms.includes(platform)) setPlatforms(platforms.filter((p) => p !== platform));
    else setPlatforms([...platforms, platform]);
  };

  const onSubmit = async (data: QuickAppType) => {
    if (platforms.length === 0) {
      setError('platforms', { type: 'manual', message: 'You must select at least one platform.' });
      return;
    }

    try {
      const response = await quickStartApp({
        ...data,
        platforms,
      });
      const project = response.parsedBody;

      if (!project) return;

      onCreate(project.app);
      onClose();
    } catch (error) {
      if (error.validationErrors?.length > 0) {
        error.validationErrors.forEach((err: CreateAppError) => {
          err.field.includes('info.')
            ? setError(err.field.slice(4), { type: 'manual', message: err.error })
            : setError(err.field, { type: 'manual', message: err.error });
        });
      } else {
        setGeneralError('Sorry, something went wrong while trying to create the app.');
      }
    }
  };

  // @ts-ignore
  const platformError = errors.platforms && `Platforms: ${errors.platforms.message}`;

  return (
    <form className={classes.wrapper} onSubmit={handleSubmit(onSubmit)}>
      <div className={classes.header}>
        <Typography variant="h2">Quick Start</Typography>
        <i className="material-icons" onClick={onModalClose}>
          close
        </i>
      </div>

      <Typography>
        Accelerate your workflow by entering the following information. All fields are required.
        Don&apos;t worry; you can change this information later.
      </Typography>

      {!userDetails && (
        <div className={classes.inputRow}>
          <TextField
            id="companyName"
            label="Company Name"
            variant="outlined"
            className="formInput"
            error={!!errors.companyName}
            helperText={getErrorMessage(errors.companyName)}
            autoComplete="off"
            focused={false}
            inputProps={register('companyName', { required: true })}
          />

          <FormControl className={classes.select} error={!!errors.country}>
            <InputLabel id="country-select-label">Country</InputLabel>
            <Controller
              control={control}
              name="country"
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  {...field}
                  variant="outlined"
                  labelId="country-select-label"
                  id="country-select"
                  label="Country"
                  onFocus={() => clearErrors('country')}
                  IconComponent={ExpandMoreIcon}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    getContentAnchorEl: null,
                  }}
                >
                  {countries.map((country) => (
                    <MenuItem key={country.value} value={country.value}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
            {errors.country && (
              <FormHelperText className="select-helper">
                {getErrorMessage(errors.country)}
              </FormHelperText>
            )}
          </FormControl>
        </div>
      )}

      <div className={classes.inputRow}>
        <TextField
          inputProps={register('name', { required: true })}
          id="name"
          label="Application Name"
          variant="outlined"
          className="formInput"
          error={!!errors.name}
          helperText={errors.name ? getErrorMessage(errors.name) : ''}
          autoComplete="off"
          focused={false}
        />
        <TextField
          inputProps={register('website', { required: true, validate: formValidators.website })}
          id="website"
          label="Application Website"
          variant="outlined"
          className="formInput"
          error={!!errors.website}
          helperText={errors.website ? getErrorMessage(errors.website) : ''}
          autoComplete="off"
          focused={false}
        />
      </div>

      <div className={classes.inputRow}>
        <TooltipInput
          tooltipText={TOOLTIPS.appNamespace}
          card
          inputProps={register('namespace', { required: true, validate: formValidators.namespace })}
          id="namespace"
          label="Application Namespace"
          variant="outlined"
          className="formInput"
          error={!!errors.namespace}
          helperText={errors.namespace ? getErrorMessage(errors.namespace) : ''}
          autoComplete="off"
          focused={false}
        />
      </div>

      <div className={classes.platforms}>
        <Typography variant="subtitle1">Platforms</Typography>

        <div className={classes.platformButtons}>
          <PlatformButton
            selected={platforms.includes('Web')}
            name="Web"
            Image={WebImage}
            onClick={() => onPlatformClick('Web')}
          />
          <PlatformButton
            selected={platforms.includes('Android')}
            name="Android"
            Image={AndroidImage}
            onClick={() => onPlatformClick('Android')}
          />
          <PlatformButton
            selected={platforms.includes('iOS')}
            name="iOS"
            Image={AppleImage}
            onClick={() => onPlatformClick('iOS')}
          />
        </div>
      </div>

      {platformError && <Typography color="error">{platformError}</Typography>}
      {generalError && <Typography color="error">{generalError}</Typography>}

      <DSButton type="submit">Next</DSButton>
    </form>
  );
};

export default CreateAppModal;
